import React from 'react';
import { Button } from './ui/button';
import { 
  Shield, 
  Users, 
  Package, 
  Zap,
  Sparkles,
  Code,
  CheckCircle,
  ArrowRight
} from 'lucide-react';
import DashboardPageLayout from './DashboardPageLayout';
import FormSection from './FormSection';

const OperationsDashboard = ({ onNavigate }) => {
  return (
    <DashboardPageLayout
      title="Welcome to BookingFlows"
      subtitle="Make booking seamless for your industry"
    >
      <div className="space-y-6">
        {/* About BookingFlows */}
        <div className="p-8 border border-gray-200 rounded-2xl" style={{backgroundColor: '#EDEFEB'}}>
          <h2 className="text-2xl font-semibold text-gray-900 mb-3">We make booking as easy and seamless as possible</h2>
          <p className="text-gray-700 text-base leading-relaxed">
            BookingFlows is the perfect booking software for companies small or large. We handle the complexity so you can focus on your business.
          </p>
        </div>

        {/* How It Works */}
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">How It Works</h2>
          <p className="text-gray-600 text-base">Set up in 4 simple steps, then start taking bookings</p>
        </div>

        {/* Step 1 */}
        <FormSection
          stepNumber={1}
          title="Build Roles & Team"
          description="Define team positions and add your team members"
          color="#3B82F6"
        >
        </FormSection>

        {/* Step 2 */}
        <FormSection
          stepNumber={2}
          title="Add Assets"
          description="Register your resources (boats, venues, equipment)"
          color="#10B981"
        >
        </FormSection>

        {/* Step 3 */}
        <FormSection
          stepNumber={3}
          title="Create Services"
          description="Build booking widgets you can embed on your website or share as a link"
          color="#9333EA"
        >
        </FormSection>

        {/* Step 4 */}
        <FormSection
          stepNumber={4}
          title="Customers Book, You Manage"
          description="Bookings appear on your calendar. Share customer tickets for waivers, reviews, and QR code check-ins"
          color="#F97316"
        >
        </FormSection>

        {/* What Makes Us Different */}
        <div className="mt-12 mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">What Makes Us Different</h2>
          <p className="text-gray-600 text-base">Built for real booking operations</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="p-6 bg-white border border-gray-200 rounded-2xl hover:border-gray-300 transition-colors">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0 bg-blue-50 text-blue-600">
                <Code className="w-6 h-6" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 text-base mb-2">Embeddable Widgets</h3>
                <p className="text-base text-gray-600 leading-relaxed">Add booking to your website with payment processing</p>
              </div>
            </div>
          </div>

          <div className="p-6 bg-white border border-gray-200 rounded-2xl hover:border-gray-300 transition-colors">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0 bg-green-50 text-green-600">
                <CheckCircle className="w-6 h-6" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 text-base mb-2">Customer Tickets</h3>
                <p className="text-base text-gray-600 leading-relaxed">Branded tickets with QR codes for waivers and check-ins</p>
              </div>
            </div>
          </div>

          <div className="p-6 bg-white border border-gray-200 rounded-2xl hover:border-gray-300 transition-colors">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0 bg-purple-50 text-purple-600">
                <Sparkles className="w-6 h-6" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 text-base mb-2">AI Auto-Scheduler</h3>
                <p className="text-base text-gray-600 leading-relaxed">Intelligent assignment based on availability and skills</p>
              </div>
            </div>
          </div>

          <div className="p-6 bg-white border border-gray-200 rounded-2xl hover:border-gray-300 transition-colors">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0 bg-orange-50 text-orange-600">
                <Shield className="w-6 h-6" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 text-base mb-2">Digital Waivers</h3>
                <p className="text-base text-gray-600 leading-relaxed">Legally-binding signatures with verification</p>
              </div>
            </div>
          </div>
        </div>

        {/* Ready to Get Started - Cleaned up */}
        <div className="p-10 bg-blue-600 text-white rounded-2xl text-center mt-12">
          <h3 className="text-2xl font-bold mb-3">Ready to Get Started?</h3>
          <p className="text-blue-100 mb-6 text-base">Set up your foundation and start taking bookings</p>
          <button 
            onClick={() => onNavigate('roles')}
            className="inline-flex items-center gap-2 px-8 py-3 bg-white text-blue-600 hover:bg-gray-100 font-semibold text-base rounded-md shadow-none transition-colors"
          >
            <Shield className="w-5 h-5" />
            Start with Step 1: Create Roles
          </button>
        </div>
      </div>
    </DashboardPageLayout>
  );
};

export default OperationsDashboard;
